import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LinkButton } from '@communityshare/ui-shared';
import { TrashIcon } from '@heroicons/react/24/outline';

/**
 * Two-step delete: the first click asks for confirmation inline, the second
 * deletes. Escape or Cancel backs out and returns focus to the Delete button.
 */
ConfirmDeleteButton.propTypes = {
  title: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  deleting: PropTypes.bool,
  disabled: PropTypes.bool
};

export default function ConfirmDeleteButton(props) {
  const { title, onConfirm, deleting = false, disabled = false } = props;
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const confirmRef = useRef(null);
  const triggerRef = useRef(null);
  const returnFocus = useRef(false);

  useEffect(() => {
    if (confirming && confirmRef.current) {
      confirmRef.current.focus();
    } else if (!confirming && returnFocus.current && triggerRef.current) {
      returnFocus.current = false;
      triggerRef.current.focus();
    }
  }, [confirming]);

  function cancel() {
    returnFocus.current = true;
    setConfirming(false);
  }

  if (!confirming) {
    return (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setConfirming(true)}
        disabled={disabled}
        aria-label={t('staff_videos.list.delete_label', { title })}
        className="inline-flex items-center gap-1 py-1 rounded text-base text-red-600 lg:hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <TrashIcon className="size-4 flex-shrink-0" aria-hidden="true" />
        <span>{t('staff_videos.list.delete')}</span>
      </button>
    );
  }

  return (
    <span
      className="inline-flex flex-wrap items-center gap-2"
      onKeyDown={(event) => {
        if (event.key === 'Escape') cancel();
      }}
    >
      <span className="text-sm text-cs-gray-900">{t('staff_videos.list.confirm_delete')}</span>
      <button
        ref={confirmRef}
        type="button"
        onClick={onConfirm}
        disabled={deleting}
        aria-label={t('staff_videos.list.confirm_delete_yes_label', { title })}
        className="py-1 px-2 rounded text-sm font-semibold text-white bg-red-600 hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {deleting ? t('staff_videos.list.deleting') : t('staff_videos.list.confirm_delete_yes')}
      </button>
      <LinkButton
        label={t('staff_videos.list.confirm_delete_cancel')}
        icon="none"
        disabled={deleting}
        onClick={cancel}
      />
    </span>
  );
}
